import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';

// Editorial theme colors
const COLORS = {
  cream: '#FAF8F5',
  charcoal: '#2D2A26',
  terracotta: '#C45D3A',
  border: '#2D2A26'
};

// Get API base URL
const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:5000';
    }
    return 'https://api.visibeen.com';
  }
  return 'https://api.visibeen.com';
};

const SectionContainer = styled(Box)({
  backgroundColor: COLORS.cream,
  borderBottom: `1px solid ${COLORS.charcoal}`
});

const SectionHeader = styled(Box)({
  padding: '48px 64px 32px',
  borderBottom: `1px solid ${COLORS.charcoal}`,
  '@media (max-width: 768px)': {
    padding: '32px 20px 24px'
  }
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: '42px',
  fontWeight: 700,
  color: COLORS.charcoal,
  letterSpacing: '-1px',
  '@media (max-width: 768px)': {
    fontSize: '32px'
  }
});

const SectionSubtitle = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  color: COLORS.charcoal,
  opacity: 0.6,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  marginTop: '8px'
});

const ContentWrapper = styled(Box)({
  padding: '48px 64px',
  '@media (max-width: 768px)': {
    padding: '32px 20px'
  }
});

const ListingsGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '24px',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr'
  }
});

const ListingCard = styled(Link)({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${COLORS.charcoal}`,
  backgroundColor: COLORS.cream,
  textDecoration: 'none',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(45, 42, 38, 0.15)',
    '& .listing-title': {
      color: COLORS.terracotta
    }
  }
});

const ListingImage = styled(Box)({
  width: '100%',
  height: '160px',
  backgroundColor: `${COLORS.charcoal}10`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  }
});

const PlaceholderIcon = styled(Box)({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  backgroundColor: `${COLORS.charcoal}15`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: COLORS.charcoal,
  fontSize: '24px',
  fontFamily: "'Playfair Display', Georgia, serif",
  fontWeight: 700
});

const ListingContent = styled(Box)({
  padding: '20px',
  borderTop: `1px solid ${COLORS.charcoal}20`
});

const ListingTitle = styled(Typography)({
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: '18px',
  fontWeight: 600,
  color: COLORS.charcoal,
  marginBottom: '8px',
  transition: 'color 0.2s ease',
  lineHeight: 1.3,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
});

const ListingCategory = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '11px',
  fontWeight: 600,
  color: COLORS.terracotta,
  textTransform: 'uppercase',
  letterSpacing: '1.5px',
  marginBottom: '8px'
});

const ListingAddress = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '13px',
  color: COLORS.charcoal,
  opacity: 0.7,
  lineHeight: 1.4,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
});

const ListingMeta = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginTop: '12px',
  paddingTop: '12px',
  borderTop: `1px dashed ${COLORS.charcoal}20`
});

const RatingBadge = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: COLORS.charcoal
});

const LocationBadge = styled(Box)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '12px',
  color: COLORS.charcoal,
  opacity: 0.6
});

const LoadingWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '48px',
  color: COLORS.charcoal
});

const EmptyMessage = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  color: COLORS.charcoal,
  opacity: 0.6,
  textAlign: 'center',
  padding: '32px'
});

const ViewAllLink = styled(Link)({
  display: 'inline-block',
  marginTop: '32px',
  padding: '14px 32px',
  border: `1px solid ${COLORS.charcoal}`,
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '13px',
  fontWeight: 600,
  color: COLORS.charcoal,
  textTransform: 'uppercase',
  letterSpacing: '1.5px',
  textDecoration: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: COLORS.charcoal,
    color: COLORS.cream
  }
});

const EditorialRelatedServices = ({ businessInfo, contactInfo, categories, currentSlug }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationInfo, setLocationInfo] = useState({ city: null, state: null, category: null });

  useEffect(() => {
    const fetchRelatedListings = async () => {
      try {
        setLoading(true);
        setError(null);

        // Extract category from businessInfo or categories prop
        let category = null;
        if (Array.isArray(categories) && categories.length > 0) {
          category = typeof categories[0] === 'string' ? categories[0] : categories[0]?.displayName || categories[0]?.name;
        }
        if (!category && businessInfo?.category) {
          category = businessInfo.category;
        }

        // Extract location from contactInfo
        let city = null;
        let state = null;

        if (contactInfo?.address) {
          // Try to parse city and state from address string
          const addressParts = contactInfo.address.split(',').map(p => p.trim());
          if (addressParts.length >= 2) {
            city = addressParts[addressParts.length - 3] || addressParts[0];
            state = addressParts[addressParts.length - 2];
          }
        }

        if (!category) {
          setLoading(false);
          return;
        }

        setLocationInfo({ city, state, category });

        // Build query params
        const params = new URLSearchParams();
        params.append('category', category);
        if (city) params.append('city', city);
        if (state) params.append('state', state);
        if (currentSlug) params.append('excludeSlug', currentSlug);
        // Also exclude by business name to ensure current business is not shown
        if (businessInfo?.name) params.append('excludeTitle', businessInfo.name);
        params.append('limit', '6');

        const apiUrl = `${getApiBaseUrl()}/api/v1/directory/related?${params.toString()}`;
        const response = await fetch(apiUrl);

        if (!response.ok) {
          throw new Error('Failed to fetch related services');
        }

        const data = await response.json();

        if (data.success && data.listings) {
          setListings(data.listings);
          if (data.city) setLocationInfo(prev => ({ ...prev, city: data.city }));
          if (data.state) setLocationInfo(prev => ({ ...prev, state: data.state }));
        }
      } catch (err) {
        console.error('[EditorialRelatedServices] Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedListings();
  }, [businessInfo, contactInfo, categories, currentSlug]);

  // Don't render if no category available
  if (!locationInfo.category && !loading) {
    return null;
  }

  // Don't render if no listings found (after loading)
  if (!loading && listings.length === 0) {
    return null;
  }

  // Build view all URL - use state level
  const getViewAllUrl = () => {
    if (locationInfo.state && locationInfo.category) {
      const stateSlug = locationInfo.state.toLowerCase().replace(/\s+/g, '-');
      // Link to state page with category filter
      return `/in/${stateSlug}`;
    }
    return '/';
  };

  return (
    <SectionContainer id="related-services">
      <SectionHeader>
        <SectionSubtitle>Discover More</SectionSubtitle>
        <SectionTitle>
          Related Services in Cities
        </SectionTitle>
      </SectionHeader>

      <ContentWrapper>
        {loading ? (
          <LoadingWrapper>
            <CircularProgress size={32} sx={{ color: COLORS.charcoal }} />
          </LoadingWrapper>
        ) : error ? (
          <EmptyMessage>Unable to load related services</EmptyMessage>
        ) : (
          <>
            <ListingsGrid>
              {listings.map((listing) => (
                <ListingCard
                  key={listing.id}
                  to={listing.url || '/'}
                >
                  <ListingImage>
                    {listing.logo ? (
                      <img src={listing.logo} alt={listing.title} />
                    ) : (
                      <PlaceholderIcon>
                        {listing.title?.charAt(0)?.toUpperCase() || 'B'}
                      </PlaceholderIcon>
                    )}
                  </ListingImage>
                  <ListingContent>
                    <ListingCategory>{listing.category || 'Business'}</ListingCategory>
                    <ListingTitle className="listing-title">{listing.title}</ListingTitle>
                    {listing.address && (
                      <ListingAddress>{listing.address}</ListingAddress>
                    )}
                    <ListingMeta>
                      {listing.rating && (
                        <RatingBadge>
                          <span style={{ color: '#FFB800' }}>★</span>
                          {listing.rating}
                        </RatingBadge>
                      )}
                      {listing.city && (
                        <LocationBadge>{listing.city}</LocationBadge>
                      )}
                    </ListingMeta>
                  </ListingContent>
                </ListingCard>
              ))}
            </ListingsGrid>

            <Box sx={{ textAlign: 'center' }}>
              <ViewAllLink to={getViewAllUrl()}>
                View All
              </ViewAllLink>
            </Box>
          </>
        )}
      </ContentWrapper>
    </SectionContainer>
  );
};

export default EditorialRelatedServices;
